import WavesurferPlayer from "@wavesurfer/react"
import { Pause, Play } from "lucide-react"
import { useMemo, useState } from "react"
import WaveSurfer from "wavesurfer.js"
import { Button } from "../../atoms/Button"
import { useTheme } from "../../theme"
import { cn } from "../../utils"

interface AudioPlayerProps {
  src: string
  className?: string
  isUser: boolean
}

export function AudioPlayer({ src, className, isUser }: AudioPlayerProps) {
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const { theme } = useTheme()

  const waveColor = useMemo(() => {
    if (isUser) {
      return theme === "dark" ? "rgb(215, 215, 215)" : "rgb(215, 215, 215)"
    }
    return theme === "dark" ? "rgb(100, 100, 100)" : "rgb(100, 100, 100)"
  }, [theme, isUser])

  const progressColor = useMemo(() => {
    if (isUser) {
      return theme === "dark" ? "rgb(255, 255, 255)" : "rgb(255, 255, 255)"
    }
    return theme === "dark" ? "rgb(255, 255, 255)" : "rgb(0, 0, 0)"
  }, [theme, isUser])

  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const onReady = (ws: WaveSurfer) => {
    setWavesurfer(ws)
    setIsPlaying(false)
    setDuration(ws.getDuration())
    setCurrentTime(ws.getCurrentTime())
  }

  const onTimeupdate = (ws: WaveSurfer) => {
    setCurrentTime(ws.getCurrentTime())
  }

  const togglePlayPause = () => {
    wavesurfer && wavesurfer.playPause()
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  return (
    <div className={cn("flex flex-col w-full", className)}>
      <div className="flex items-center justify-between gap-2">
        <Button onClick={togglePlayPause} variant="ghost" size="icon" className="h-8 w-8">
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
        <div className="flex-1">
          <WavesurferPlayer
            height={32}
            waveColor={waveColor}
            progressColor={progressColor}
            dragToSeek
            fillParent
            url={src}
            cursorWidth={1.5}
            interact
            onReady={onReady}
            onTimeupdate={onTimeupdate}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            cursorColor={waveColor}
          />
        </div>
        <div className="text-xs w-[70px] text-right">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>
    </div>
  )
}
